import {
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@ondit/ui";
import { Building, Check, ChevronDown, Plus, Search, Settings2 } from "lucide-react";
import { useT } from "~/core/i18n";
import { initials } from "~/shell/initials";

/** A brand's mark: square, where a person's is round, so the two rows at the
 *  top and the foot of the column cannot be mistaken for each other. */
const MARK =
	"flex size-(--s-9) shrink-0 items-center justify-center rounded-control bg-[var(--surface-active)] text-fg-2 text-label leading-none";

interface SwitchableBrand {
	id: string;
	name: string;
	logo_url?: string | null;
}

/**
 * Which brand this workspace is looking at — and the way to another, or to a
 * different account entirely.
 *
 * The first row of the sidebar in both modules, and deliberately the same
 * component in both: it is the row that answers "whose numbers am I looking
 * at", and an answer that is drawn two different ways depending on which half
 * of the product you are in is an answer nobody trusts.
 *
 * A menu even when the account has one brand, because the second one is added
 * from here and an account with a single brand is exactly the account that
 * needs to find that.
 */
export function BrandSwitcher({
	brandId,
	brands,
	orgs,
	collapsed = false,
	canAddBrand = false,
	onSelectBrand,
	onSelectOrg,
	onAddBrand,
	onOpenSettings,
	onSearch,
}: {
	brandId: string;
	brands: readonly SwitchableBrand[];
	/** Only rendered when there is more than one — see below. */
	orgs?: readonly (SwitchableBrand & { current?: boolean })[];
	collapsed?: boolean;
	canAddBrand?: boolean;
	onSelectBrand: (id: string) => void;
	onSelectOrg?: (id: string) => void;
	onAddBrand?: () => void;
	/** The brand's own settings, which is what this menu is already about. */
	onOpenSettings?: () => void;
	onSearch?: () => void;
}) {
	const t = useT();
	const current = brands.find((one) => one.id === brandId) ?? brands[0];
	const name = current?.name ?? t("brand.none");

	return (
		// The row is the component's own, not the host's. `flex-1` on the trigger
		// is what makes the name take the width the mark leaves — and in a flex
		// *column*, which is what a sidebar is, that same class makes it take the
		// height too. It rendered as a 300px grey slab.
		<div className="flex min-w-0 flex-1 items-center">
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant="ghost"
						className={`min-w-0 flex-1 gap-2 ${collapsed ? "px-0" : "px-1.5"}`}
						aria-label={t("brand.switch")}
					>
						<span aria-hidden className={MARK}>
							{current?.logo_url ? (
								<img src={current.logo_url} alt="" className="size-full object-contain" />
							) : (
								initials(name)
							)}
						</span>
						{!collapsed && (
							<>
								<span className="min-w-0 flex-1 truncate text-left text-fg">{name}</span>
								<ChevronDown className="size-(--s-6) text-[var(--fg-3)]" />
							</>
						)}
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start">
					{brands.map((brand) => (
						<DropdownMenuItem key={brand.id} onSelect={() => onSelectBrand(brand.id)}>
							<span aria-hidden className={MARK}>
								{brand.logo_url ? (
									<img src={brand.logo_url} alt="" className="size-full object-contain" />
								) : (
									initials(brand.name)
								)}
							</span>
							<span className="min-w-0 flex-1 truncate">{brand.name}</span>
							{brand.id === current?.id ? <Check /> : null}
						</DropdownMenuItem>
					))}
					{onSearch ? (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuItem onSelect={onSearch}>
								<Search />
								{t("nav.search")}
							</DropdownMenuItem>
						</>
					) : null}
					{onOpenSettings ? (
						<>
							<DropdownMenuSeparator />
							{/*
							 * Settings belongs to the brand rather than to either module,
							 * and it was a row at the foot of Analytics' navigation — so
							 * it read as Analytics', and the Agent's column had no way to
							 * it at all except through the account menu, which is the
							 * person's and not the brand's.
							 */}
							<DropdownMenuItem onSelect={onOpenSettings}>
								<Settings2 />
								{t("nav.settings")}
							</DropdownMenuItem>
						</>
					) : null}
					{canAddBrand && onAddBrand ? (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuItem onSelect={onAddBrand}>
								<Plus />
								{t("brand.add")}
							</DropdownMenuItem>
						</>
					) : null}
					{/*
					 * Only for somebody who is in more than one. Everybody gets their own
					 * workspace at sign-up, and a second appears the day a colleague
					 * invites them into theirs — without this row the application would
					 * show one of the two and give no way to reach the other.
					 */}
					{orgs && orgs.length > 1 && onSelectOrg ? (
						<>
							<DropdownMenuSeparator />
							{orgs.map((org) => (
								<DropdownMenuItem key={org.id} onSelect={() => onSelectOrg(org.id)}>
									<Building />
									<span className="min-w-0 flex-1 truncate">{org.name}</span>
									{org.current ? <Check /> : null}
								</DropdownMenuItem>
							))}
						</>
					) : null}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}
